import React, {useState, useCallback, Fragment} from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { Baby, Brush, ChevronLeft, Flower, Gift, Newspaper, Sparkles, Tag, User, Zap } from "lucide-react";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

const menuItemsObject: any = {
    "cosmetics": <Brush aria-hidden="true" />,
    "personal-care": <Sparkles aria-hidden="true" />,
    "mens-products": <User aria-hidden="true" />,
    "electrical-care-tools": <Zap aria-hidden="true" />,
    "mother-and-baby": <Baby aria-hidden="true" />,
    "fragrances": <Flower aria-hidden="true" />,
    "gift-card": <Gift aria-hidden="true" />,
    "brand": <Tag aria-hidden="true" />,
    "news": <Newspaper aria-hidden="true" />,
};

const MobileMegaMenu: React.FC<{ data: any, categoriesProductCount: any }> = ({ data, categoriesProductCount }) => {
    const [expandedItem, setExpandedItem] = useState<number | null>(0);
    const [expandedAccordion, setExpandedAccordion] = useState<string | null>(null);

    const toggleItem = useCallback(
        (index: number) => {
            setExpandedItem(prev => (prev === index ? null : index));
            setExpandedAccordion(null);
        },
        []
    );

    if (!data) return null;

    return (
        <div className="w-full h-[calc(100vh-88px)] pt-7 z-[100] menu-show-anime flex">
            <nav className="w-[90px] h-full flex flex-col" aria-label="Main Menu">
                {data.map((item: any, index: any) => (
                    <Fragment key={index}>
                        {item?.slug !== "gift-card" ? (
                            <Button
                                key={item.title}
                                variant="none"
                                className={cn(
                                    "h-[66px] rounded-none border-t border-l flex flex-col bg-gray-100 duration-200 items-center justify-center p-1",
                                    index === data.length - 1 ? "border-y" : "",
                                    expandedItem === index ? "!bg-white !text-orange-primary" : ""
                                )}
                                onClick={() => toggleItem(index)}
                                aria-expanded={expandedItem === index}
                                aria-controls={`submenu-${index}`}
                            >
                                {menuItemsObject[item?.slug]}
                                <span className="text-[10px] w-full truncate mt-1">{item?.title}</span>
                            </Button>
                        ) : (
                            <>
                                <Link
                                    href={`/category/${item.slug}`}
                                    prefetch={false}
                                    key={item.title}
                                    className={cn(
                                        "h-[66px] rounded-none border-t border-l flex flex-col bg-gray-100 duration-200 items-center justify-center p-1",
                                        index === data.length - 1 ? "border-y" : "",
                                        expandedItem === index ? "!bg-white !text-orange-primary" : ""
                                    )}
                                    onClick={() => toggleItem(index)}
                                    aria-expanded={expandedItem === index}
                                    aria-controls={`submenu-${index}`}
                                >
                                    {menuItemsObject[item?.slug]}
                                    <span className="text-[10px] text-center w-full truncate mt-1">{item?.title}</span>
                                </Link>
                                <Link
                                    href={`/brand`}
                                    prefetch={false}
                                    key={"brand"}
                                    className={cn(
                                        "h-[66px] rounded-none border-t border-l flex flex-col bg-gray-100 duration-200 items-center justify-center p-1",
                                        index === data.length - 1 ? "border-y" : "",
                                    )}
                                    onClick={() => toggleItem(index)}
                                    aria-expanded={expandedItem === index}
                                    aria-controls={`submenu-${index}`}
                                >
                                    {menuItemsObject["brand"]}
                                    <span className="text-[10px] text-center w-full truncate mt-1">برند ها</span>
                                </Link>
                                <Link
                                    href={`https://elanza.com/mag/`}
                                    prefetch={false}
                                    target="_blank"
                                    key={"mag"}
                                    className={cn(
                                        "h-[66px] rounded-none border-t border-l flex flex-col bg-gray-100 duration-200 items-center justify-center p-1",
                                        index === data.length - 1 ? "border-y" : "",
                                        expandedItem === index ? "!bg-white !text-orange-primary" : ""
                                    )}
                                    onClick={() => toggleItem(index)}
                                    aria-expanded={expandedItem === index}
                                    aria-controls={`submenu-${index}`}
                                >
                                    {menuItemsObject["news"]}
                                    <span className="text-[10px] text-center w-full truncate mt-1">مجله</span>
                                </Link>
                            </>
                        )}
                    </Fragment>
                ))}
            </nav>
            <ScrollArea dir="rtl" className="flex-1 h-full overflow-y-auto pb-20">
                <div className="pt-5 w-full flex items-center justify-start">
                    <Link
                        href={`/category/${expandedItem !== null && data[expandedItem]?.slug}`}
                        prefetch={false}
                        className="text-orange-500 px-4 w-full gap-1 flex items-center justify-start font-medium text-xs"
                    >
                        {expandedItem !== null && data[expandedItem]?.title}
                        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                    </Link>
                </div>
                <div className="p-4" id={`submenu-${expandedItem}`}>
                    {expandedItem !== null && data[expandedItem]?.children && (
                        <Accordion
                            type="single"
                            collapsible
                            className="w-full"
                            value={expandedAccordion || undefined}
                            onValueChange={setExpandedAccordion}
                        >
                            {data[expandedItem].children.map((subItem: any, index: any) => (
                                <AccordionItem value={`item-${index}`} key={subItem.slug}>
                                    <AccordionTrigger className="text-right">
                                        {subItem.title}
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <div className="pl-4 py-1">
                                            <Link
                                                prefetch={false}
                                                className={"py-1 text-xs text-orange-primary w-full flex flex-wrap gap-1"}
                                                href={`/category/${subItem.slug}`} as={`/category/${subItem.slug}`}>
                                                مشاهده
                                                {" "}
                                                {subItem.title}
                                                <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                                            </Link>
                                            {subItem.children ? (
                                                subItem.children.map((child: any) => (
                                                    categoriesProductCount[child?.id] > 0 && (
                                                        <Link
                                                            key={child.slug}
                                                            href={`/category/${child.slug}`}
                                                            prefetch={false}
                                                            className="text-sm text-gray-500 block py-1"
                                                        >
                                                            {child.title}
                                                        </Link>
                                                    )
                                                ))
                                            ) : (
                                                <Link
                                                    href={`/category/${subItem.slug}`}
                                                    prefetch={false}
                                                    className="text-sm text-gray-500"
                                                >
                                                    {subItem.title}
                                                </Link>
                                            )}
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    )}
                </div>
            </ScrollArea>
        </div>
    );
};

MobileMegaMenu.displayName = "MobileMegaMenu";

export default MobileMegaMenu;
